import React, { useState, useEffect, useCallback } from 'react';
import { FiCpu, FiRefreshCw, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import api from '../../services/api';
import {
  buildVprDeepseekCrossMatchPayload,
  parseEngineResultFromManualBlob,
  VPR_DEEPSEEK_API_KEY_STORAGE,
  VPR_DEEPSEEK_API_TIMEOUT_MS,
} from '../../utils/vprManualDeepseek';
import { normalizeVprCrossMatchMode } from '../../utils/vprCrossMatchNd';
import {
  fillManualCrossFioFromDetect,
  listManualCrossFioMissingSlotLabels,
  MANUAL_CROSS_FIO_VALIDATION_HINT,
} from '../../utils/vprCrossMatchFio';

export default function VprManualDeepseekPanel({
  loadedIndices,
  manualFiles,
  manualCrossFileMatch,
  manualFileConfigs,
  manualRenames,
  manualMainFileIndex = 0,
  manualResult,
  deepseekResult,
  onDeepseekResult,
  onAfterAnalysis,
  onError,
  onClearError,
  compact,
}) {
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('comparison');
  const [statusMsg, setStatusMsg] = useState('');
  const [engineFromExcel, setEngineFromExcel] = useState(null);

  const result = deepseekResult;
  const setResult = onDeepseekResult;
  const crossMode = normalizeVprCrossMatchMode(manualCrossFileMatch?.matchMode);
  const isNdMode = crossMode === 'phone_nd';
  const isLeadingMode = crossMode === 'leading_phone_fio';
  const labelNoCall = isNdMode ? 'Дозвонились (не звонить)' : 'Не звонить';
  const labelCall = isNdMode ? 'Перезвонить (НД)' : isLeadingMode ? 'Только в ведущем' : 'Звонить';
  const labelMatch = isLeadingMode ? 'Совпадения с ведущим' : null;

  useEffect(() => {
    try {
      setApiKey(localStorage.getItem(VPR_DEEPSEEK_API_KEY_STORAGE) || '');
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!manualResult?.sheets?.length) {
      setEngineFromExcel(null);
      return;
    }
    setEngineFromExcel(parseEngineResultFromManualBlob(manualResult.sheets));
  }, [manualResult]);

  const saveApiKey = useCallback((v) => {
    setApiKey(v);
    try {
      if (v.trim()) localStorage.setItem(VPR_DEEPSEEK_API_KEY_STORAGE, v.trim());
      else localStorage.removeItem(VPR_DEEPSEEK_API_KEY_STORAGE);
    } catch (_) {}
  }, []);

  const runAnalysis = async () => {
    if (loadedIndices.length < 2) {
      const msg = 'Загрузите и разнесите минимум 2 источника (слота) с телефонами.';
      onError(msg);
      setStatusMsg(msg);
      return;
    }
    if (!apiKey.trim()) {
      const msg = 'Укажите API-ключ DeepSeek.';
      onError(msg);
      setStatusMsg(msg);
      return;
    }
    const missingPhone = loadedIndices.some((i) => {
      const p = manualCrossFileMatch.phoneBySlot[i] || {};
      return p.col == null || p.col === '';
    });
    if (missingPhone) {
      const msg = 'Для каждого слота укажите столбец телефона в блоке «Совпадения по телефону / ФИО».';
      onError(msg);
      setStatusMsg(msg);
      return;
    }
    if (crossMode === 'phone_nd' || crossMode === 'leading_phone_fio') {
      const fioFill = fillManualCrossFioFromDetect(
        manualCrossFileMatch.fioBySlot,
        loadedIndices,
        manualFiles,
        manualRenames,
      );
      const missingFio = listManualCrossFioMissingSlotLabels(
        loadedIndices,
        fioFill.fioBySlot,
        manualFiles,
        manualRenames,
      );
      if (missingFio.length) {
        const msg = `Укажите столбец ФИО / контакта для: ${missingFio.join(', ')}. ${MANUAL_CROSS_FIO_VALIDATION_HINT}`;
        onError(msg);
        setStatusMsg(msg);
        return;
      }
      if (crossMode === 'phone_nd') {
        const missingNd = loadedIndices.some((i) => {
          const nd = manualCrossFileMatch.ndBySlot?.[i] || {};
          return nd.col == null || nd.col === '';
        });
        if (missingNd) {
          const msg = 'В режиме «Телефон + ФИО + НД» укажите столбец статуса (нд, недозвон) в каждом слоте.';
          onError(msg);
          setStatusMsg(msg);
          return;
        }
      }
    }
    setLoading(true);
    onClearError();
    setStatusMsg('Запрос к DeepSeek… подождите до 2 минут.');
    try {
      const payload = buildVprDeepseekCrossMatchPayload(
        loadedIndices,
        manualFiles,
        manualCrossFileMatch,
        manualFileConfigs,
        manualRenames,
        manualMainFileIndex,
      );
      const { data } = await api.post(
        '/api/vpr-deepseek-cross-match',
        { ...payload, apiKey: apiKey.trim() },
        { timeout: VPR_DEEPSEEK_API_TIMEOUT_MS },
      );
      setResult(data);
      setTab('comparison');
      if (typeof onAfterAnalysis === 'function') onAfterAnalysis(data);
      const aiOk = data?.ai?.ok;
      const cmp = data?.comparison;
      if (aiOk && cmp?.available) {
        setStatusMsg(
          `Готово. Дозвонились (не звонить): движок ${cmp.noCall.engine}, ИИ ${cmp.noCall.ai}, совпали ${cmp.noCall.both}. Перезвонить (НД): движок ${cmp.callOnly.engine}, ИИ ${cmp.callOnly.ai}.`,
        );
      } else if (data?.engine) {
        setStatusMsg('Движок посчитан. ИИ не ответил — смотрите вкладку «Движок (сверка)» или повторите запрос.');
      } else {
        setStatusMsg('Ответ получен, но данных нет.');
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Ошибка запроса к DeepSeek.';
      onError(msg);
      setStatusMsg(msg);
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const eng = result?.engine;
  const ai = result?.ai;
  const cmp = result?.comparison;
  const listStyle = {
    maxHeight: compact ? 160 : 220,
    overflowY: 'auto',
    fontSize: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '8px',
    background: '#fff',
    userSelect: 'text',
    WebkitUserSelect: 'text',
  };

  const pad = compact ? '8px 10px' : '12px 14px';

  return (
    <div
      style={{
        marginBottom: compact ? '8px' : '14px',
        padding: pad,
        borderRadius: '10px',
        border: '2px solid rgba(99, 102, 241, 0.45)',
        background: 'rgba(238, 242, 255, 0.85)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <FiCpu style={{ color: '#4f46e5' }} />
        <span style={{ fontSize: compact ? '13px' : '14px', fontWeight: 700, color: '#3730a3' }}>ИИ-ассистент DeepSeek</span>
      </div>
      {!compact && (
        <p style={{ margin: '0 0 10px', fontSize: '12px', lineHeight: 1.5, color: '#475569' }}>
          После анализа в модалке «Результат» появятся вкладки <strong>«Сравнение ИИ/движок»</strong>, <strong>«ИИ — не звонить»</strong>, <strong>«ИИ — звонить»</strong> рядом с листами движка.
        </p>
      )}
      {!compact && (
        <>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#334155' }}>
            API-ключ DeepSeek (только в браузере)
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => saveApiKey(e.target.value)}
            placeholder="sk-..."
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '8px 10px',
              marginBottom: '10px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              fontSize: '13px',
            }}
          />
        </>
      )}
      <button
        type="button"
        disabled={loading || loadedIndices.length < 2}
        onClick={runAnalysis}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: compact ? '8px 12px' : '10px 16px',
          borderRadius: '8px',
          border: '2px solid #6366f1',
          background: loading ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.15)',
          color: '#4338ca',
          fontWeight: 700,
          fontSize: '13px',
          cursor: loading ? 'wait' : 'pointer',
          marginBottom: '8px',
        }}
      >
        <FiRefreshCw /> {loading ? 'Анализ…' : compact ? 'Обновить анализ ИИ' : 'Найти совпадения (ИИ + движок)'}
      </button>

      {statusMsg && (
        <p
          style={{
            margin: '0 0 10px',
            fontSize: '12px',
            lineHeight: 1.45,
            color: statusMsg.includes('Готово') ? '#166534' : '#b91c1c',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '6px',
          }}
        >
          {statusMsg.includes('Готово') ? <FiCheckCircle style={{ flexShrink: 0 }} /> : <FiAlertCircle style={{ flexShrink: 0 }} />}
          <span>{statusMsg}</span>
        </p>
      )}

      {eng && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
            {['comparison', 'engine', 'ai'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: tab === t ? '2px solid #6366f1' : '1px solid #cbd5e1',
                  background: tab === t ? '#e0e7ff' : '#fff',
                  fontWeight: 600,
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                {t === 'comparison' ? 'Сравнение' : t === 'engine' ? 'Движок' : 'DeepSeek'}
              </button>
            ))}
          </div>

          {tab === 'comparison' && cmp?.available && (
            <div style={{ fontSize: '13px', lineHeight: 1.55, color: '#0f172a' }}>
              <p style={{ margin: '0 0 8px' }}>
                <strong>{labelNoCall}:</strong> движок {cmp.noCall.engine}, ИИ {cmp.noCall.ai}, совпали {cmp.noCall.both},
                только движок {cmp.noCall.onlyEngine}, только ИИ {cmp.noCall.onlyAi}
              </p>
              <p style={{ margin: '0 0 8px' }}>
                <strong>{labelCall}:</strong> движок {cmp.callOnly.engine}, ИИ {cmp.callOnly.ai}, совпали {cmp.callOnly.both},
                только движок {cmp.callOnly.onlyEngine}, только ИИ {cmp.callOnly.onlyAi}
              </p>
              {ai?.notes && (
                <p style={{ margin: '0 0 8px', padding: '8px', background: '#f8fafc', borderRadius: '6px' }}>
                  <strong>Заметки ИИ:</strong> {ai.notes}
                </p>
              )}
              {engineFromExcel?.fromExcel && (
                <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>
                  Excel после «Запустить»: {labelNoCall.toLowerCase()} {engineFromExcel.noCall.length}, {labelCall.toLowerCase()} {engineFromExcel.callOnly.length}.
                </p>
              )}
            </div>
          )}

          {tab === 'comparison' && !cmp?.available && (
            <p style={{ fontSize: '13px', color: '#64748b' }}>
              {ai?.skipped ? 'ИИ пропущен (нет ключа).' : ai?.ok === false ? 'ИИ не вернул JSON — смотрите вкладку «Движок».' : 'Сравнение недоступно.'}
            </p>
          )}

          {tab === 'engine' && (
            <div style={listStyle}>
              <div style={{ fontWeight: 700, marginBottom: '6px' }}>{labelNoCall} ({eng.noCall?.length || 0})</div>
              {(eng.noCall || []).slice(0, 80).map((r, i) => (
                <div key={`enc-${i}`} style={{ marginBottom: '4px' }}>
                  {r.phone}{r.fio ? ` · ${r.fio}` : ''} — {(r.files || []).join(', ')}
                </div>
              ))}
              <div style={{ fontWeight: 700, margin: '10px 0 6px' }}>{labelCall} ({eng.callOnly?.length || 0})</div>
              {(eng.callOnly || []).slice(0, 80).map((r, i) => (
                <div key={`ecc-${i}`} style={{ marginBottom: '4px' }}>
                  {r.phone}{r.fio ? ` · ${r.fio}` : ''} — {(r.files || []).join(', ')}
                </div>
              ))}
            </div>
          )}

          {tab === 'ai' && ai?.ok && (
            <div style={listStyle}>
              <div style={{ fontWeight: 700, marginBottom: '6px' }}>{labelNoCall} ({ai.noCall?.length || 0})</div>
              {(ai.noCall || []).slice(0, 80).map((r, i) => (
                <div key={`ain-${i}`} style={{ marginBottom: '4px' }}>
                  {r.phone}{r.fio ? ` · ${r.fio}` : ''} — {(r.files || []).join(', ')}
                  {r.reason ? ` (${r.reason})` : ''}
                </div>
              ))}
              <div style={{ fontWeight: 700, margin: '10px 0 6px' }}>{labelCall} ({ai.callOnly?.length || 0})</div>
              {(ai.callOnly || []).slice(0, 80).map((r, i) => (
                <div key={`aic-${i}`} style={{ marginBottom: '4px' }}>
                  {r.phone}{r.fio ? ` · ${r.fio}` : ''} — {(r.files || []).join(', ')}
                </div>
              ))}
            </div>
          )}

          {tab === 'ai' && ai && !ai.ok && !ai.skipped && (
            <p style={{ color: '#b91c1c', fontSize: '13px' }}>ИИ не вернул результат. Повторите запрос или проверьте ключ.</p>
          )}
        </>
      )}
    </div>
  );
}
